import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { api, friendlyError } from '../lib/api';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const FIELDS = [
  ['name', 'Nombre del proyecto', 'text', true],
  ['city', 'Ciudad', 'text'],
  ['zone', 'Zona / sector', 'text'],
  ['address', 'Dirección', 'text'],
  ['type', 'Tipo de proyecto', 'text'],
  ['price_from', 'Precio desde (COP)', 'number'],
  ['delivery_date', 'Fecha estimada de entrega', 'text'],
  ['units_count', 'Número de unidades', 'number'],
  ['cover_url', 'URL imagen de portada', 'text'],
];

const EMPTY = { name: '', city: '', zone: '', address: '', type: '', description: '',
  price_from: '', delivery_date: '', units_count: '', cover_url: '' };

export default function DeveloperProjectForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const p = await api.developerProject(id);
        setForm({
          name: p.name || '', city: p.city || '', zone: p.zone || '', address: p.address || '',
          type: p.type || '', description: p.description || '',
          price_from: p.price_min ?? '', delivery_date: p.delivery_date || '',
          units_count: p.units_count ?? '', cover_url: p.cover_url || '',
        });
      } catch (e) { setError(friendlyError(e)); } finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || saving) return;
    setSaving(true); setError('');
    const body = {
      name: form.name.trim(),
      city: form.city || null, zone: form.zone || null, address: form.address || null,
      type: form.type || null, description: form.description || null,
      delivery_date: form.delivery_date || null, cover_url: form.cover_url || null,
      price_from: form.price_from === '' ? null : Number(form.price_from),
      units_count: form.units_count === '' ? null : parseInt(form.units_count, 10),
    };
    try {
      if (isEdit) await api.updateDeveloperProject(id, body);
      else await api.createDeveloperProject(body);
      navigate(isEdit ? `/mi-constructora/proyecto/${id}` : '/mi-constructora');
    } catch (e2) { setError(friendlyError(e2)); setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="developer-project-form">
      <SEO title={isEdit ? 'Editar proyecto · Habita' : 'Nuevo proyecto · Habita'} />
      <Link to={isEdit ? `/mi-constructora/proyecto/${id}` : '/mi-constructora'} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-habita-dark">
        <ArrowLeft className="w-4 h-4" /> {isEdit ? 'Volver al proyecto' : 'Volver a mi constructora'}
      </Link>
      <h1 className="font-serif text-2xl font-black text-habita-dark mt-3">
        {isEdit ? 'Editar proyecto' : 'Nuevo proyecto'}
      </h1>
      <p className="text-sm text-gray-500 mt-1">Se guarda como <strong>borrador</strong>. El inventario detallado llega en una fase posterior.</p>

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, type, required]) => (
          <label key={k} className={`text-sm ${k === 'name' ? 'sm:col-span-2' : ''}`}>
            <span className="text-xs uppercase tracking-widest text-gray-500">{label}{required && ' *'}</span>
            <input
              type={type} value={form[k]} required={required}
              onChange={(e) => set(k, e.target.value)} data-testid={`project-field-${k}`}
              className="mt-1 w-full h-11 px-3 rounded-xl border border-gray-200 focus:border-habita outline-none"
            />
          </label>
        ))}
        <label className="text-sm sm:col-span-2">
          <span className="text-xs uppercase tracking-widest text-gray-500">Descripción</span>
          <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={4}
            data-testid="project-field-description"
            className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-habita outline-none" />
        </label>

        {error && <p className="text-sm text-rose-600 sm:col-span-2" data-testid="project-form-error">{error}</p>}

        <div className="sm:col-span-2">
          <Button type="submit" disabled={saving} data-testid="project-save-btn"
            className="rounded-full bg-habita hover:bg-habita-dark text-white font-bold h-12 px-6">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando…' : (isEdit ? 'Guardar cambios' : 'Crear borrador')}
          </Button>
        </div>
      </form>
    </div>
  );
}
